import { motion, useReducedMotion } from "framer-motion";

/**
 * ExecutiveAdvisory — "Compass Rose"
 * A slow-rotating compass with 8 advisory tick marks (Strategy, Governance, AI,
 * Transformation, ERP, Due Diligence, Growth, Risk). A settling needle
 * communicates "senior, independent direction across the whole landscape".
 */
const AREAS = ["STRATEGY", "AI", "TRANSFORM", "GROWTH", "RISK", "GOVERNANCE", "ERP", "DILIGENCE"];

export default function AdvisoryCompass({ className = "" }) {
  const reduce = useReducedMotion();
  const cx = 200, cy = 160, rOuter = 110, rTick = 96, rInner = 72;

  return (
    <div className={`pointer-events-none select-none ${className}`} aria-hidden="true">
      <svg viewBox="0 0 400 320" className="h-full w-full" role="presentation">
        <defs>
          <radialGradient id="advc-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
          </radialGradient>
        </defs>

        <circle cx={cx} cy={cy} r={140} fill="url(#advc-glow)" />

        {/* Concentric rings */}
        {[rOuter, rOuter - 24, rOuter - 48].map((r, i) => (
          <motion.circle
            key={r} cx={cx} cy={cy} r={r} fill="none" stroke="#7DB4FF" strokeOpacity={0.18 - i * 0.04}
            initial={{ scale: 0.98, opacity: 0.5 }}
            animate={reduce ? { scale: 1 } : { scale: [0.98, 1.03, 0.98], opacity: [0.5, 0.9, 0.5] }}
            transition={reduce ? { duration: 0 } : { duration: 6 + i * 1.5, repeat: Infinity, ease: "easeInOut" }}
            style={{ transformOrigin: `${cx}px ${cy}px` }}
          />
        ))}

        {/* Tick marks + labels around the rose */}
        {AREAS.map((label, i) => {
          const angle = (i / AREAS.length) * Math.PI * 2 - Math.PI / 2;
          const x1 = cx + Math.cos(angle) * (rTick - 8);
          const y1 = cy + Math.sin(angle) * (rTick - 8);
          const x2 = cx + Math.cos(angle) * rTick;
          const y2 = cy + Math.sin(angle) * rTick;
          const tx = cx + Math.cos(angle) * (rOuter + 12);
          const ty = cy + Math.sin(angle) * (rOuter + 12) + 3;
          return (
            <g key={label}>
              <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#93C5FD" strokeOpacity="0.7" strokeWidth="1.2" />
              <text x={tx} y={ty} textAnchor="middle" fill="#CBD5E1" fontSize="8" letterSpacing="2" fontFamily="ui-sans-serif, system-ui" style={{ textTransform: "uppercase" }}>
                {label}
              </text>
            </g>
          );
        })}

        {/* Rotating needle */}
        <motion.g
          initial={{ rotate: -30 }}
          animate={reduce ? { rotate: 0 } : { rotate: [-30, 12, -8, 22, -14, 0] }}
          transition={reduce ? { duration: 0 } : { duration: 22, repeat: Infinity, ease: "easeInOut" }}
          style={{ originX: `${cx}px`, originY: `${cy}px`, transformBox: "fill-box" }}
        >
          <polygon
            points={`${cx},${cy - rInner} ${cx - 8},${cy + 6} ${cx + 8},${cy + 6}`}
            fill="#3B82F6" opacity="0.95"
          />
          <polygon
            points={`${cx},${cy + rInner - 20} ${cx - 5},${cy + 6} ${cx + 5},${cy + 6}`}
            fill="#7DB4FF" opacity="0.55"
          />
        </motion.g>

        {/* Center pivot */}
        <circle cx={cx} cy={cy} r="6" fill="#93C5FD" />
        <circle cx={cx} cy={cy} r="2.5" fill="#0F172A" />

        <text x={cx} y={cy + 128} textAnchor="middle" fill="#CBD5E1" fontSize="9" letterSpacing="2.5" fontFamily="ui-sans-serif, system-ui" style={{ textTransform: "uppercase" }}>
          DIRECTION, INDEPENDENT
        </text>
      </svg>
    </div>
  );
}
